import { inject } from '@angular/core';
import { CanActivateChildFn, Router, UrlTree } from '@angular/router';
import { MenuItem } from '../models/auth.model';

export const ACCESS_DENIED_ROUTE = '/sin-acceso';

export const permissionGuard: CanActivateChildFn = (_route, state): boolean | UrlTree => {
  const router = inject(Router);
  const requestedRoute = cleanRoute(state.url);

  if (requestedRoute === ACCESS_DENIED_ROUTE) {
    return true;
  }

  const menu = readMenu();

  if (hasRoutePermission(menu, requestedRoute)) {
    return true;
  }

  if (requestedRoute === '/dashboard' || requestedRoute === '/') {
    const fallbackRoute = getFirstAvailableRoute(menu);
    if (fallbackRoute) {
      return router.parseUrl(fallbackRoute);
    }
  }

  return router.parseUrl(ACCESS_DENIED_ROUTE);
};

export function readMenu(): MenuItem[] {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const menu: unknown = JSON.parse(localStorage.getItem('menu') ?? '[]');
    return Array.isArray(menu) ? (menu as MenuItem[]) : [];
  } catch {
    return [];
  }
}

export function hasRoutePermission(menu: MenuItem[], requestedRoute: string): boolean {
  if (checkSingleRoute(menu, requestedRoute)) {
    return true;
  }

  if (requestedRoute === '/calificaciones' || requestedRoute === '/libro-calificaciones') {
    return checkSingleRoute(menu, '/evaluaciones');
  }

  return false;
}

function checkSingleRoute(menu: MenuItem[], targetRoute: string): boolean {
  return menu.some((item) => {
    if (!item.route || item.route === '#') {
      return checkSingleRoute(item.children ?? [], targetRoute);
    }

    const allowedRoute = cleanRoute(item.route);
    const matchesRoute =
      targetRoute === allowedRoute ||
      targetRoute.startsWith(`${allowedRoute}/`) ||
      allowedRoute.startsWith(`${targetRoute}/`);

    return matchesRoute || checkSingleRoute(item.children ?? [], targetRoute);
  });
}

export function getFirstAvailableRoute(menu: MenuItem[]): string | null {
  for (const item of menu) {
    const route = item.route ? item.route.trim() : '';
    if (route && route !== '#') {
      return cleanRoute(route);
    }
    if (item.children && item.children.length > 0) {
      const childRoute = getFirstAvailableRoute(item.children);
      if (childRoute) {
        return childRoute;
      }
    }
  }
  return null;
}

export function cleanRoute(route: string): string {
  const path = route.split(/[?#]/, 1)[0].trim();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return normalizedPath.length > 1 ? normalizedPath.replace(/\/+$/, '') : normalizedPath;
}

